import { twMerge } from "tailwind-merge";
import {
  Card,
  CardContent,
  CardHeader,
  H2,
  H3,
  H5,
} from "@sellar-io/components";
import { BuyerProductGetResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import { SpinnerLegacy } from "../../components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../components/modal/Dialog";
import { useBuyerProduct } from "../../hooks/api/v2/product/useBuyerProduct";

import { StorefrontProductCard } from "../stockList/categoryPage/Storefront/StorefrontLayout/StorefrontProductCard";
import { DietaryDisplayList } from "../../components/dietaryDisplayList/DietaryDisplayList";
import { StockIndicator } from "../../components";
import { InfoCards } from "./CollabageddonCheckout";

type CollabageddonProductDialogProps = {
  active: boolean;
  onDismiss: () => void;
  ParentId: number;
  ProductItemId: number;
  price: number;
  setBestPricePromiseDialogActive: (
    product?: BuyerProductGetResponseType,
  ) => void;
};

export const CollabageddonProductDialog = (
  props: CollabageddonProductDialogProps,
) => {
  return (
    <Dialog
      fullHeight={true}
      active={props.active}
      onDismiss={props.onDismiss}
      className={"max-w-[1000px]"}
    >
      <CollabageddonProductChildDialogContent {...props} />
    </Dialog>
  );
};

const CollabageddonProductChildDialogContent = ({
  ParentId,
  ProductItemId,
  onDismiss,
  price,
  setBestPricePromiseDialogActive,
}: CollabageddonProductDialogProps) => {
  const productQuery = useBuyerProduct(ParentId, {
    enabled: !!ParentId,
  });

  const childProduct = productQuery.data?.Products.find(
    (product) => product.id === ProductItemId,
  );

  return (
    <>
      <DialogNavigation
        title={
          productQuery.isPending
            ? "Loading..."
            : productQuery.isError
              ? "Something went wrong..."
              : (productQuery.data.name ?? "")
        }
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        {productQuery.isPending ? (
          <SpinnerLegacy />
        ) : productQuery.isError ? (
          <div>Something went wrong</div>
        ) : (
          <div className={"grid gap-2 overflow-y-auto md:grid-cols-2"}>
            <StorefrontProductCard
              supplier={productQuery.data.Supplier}
              item={productQuery.data}
              setBestPricePromiseDialogActive={() => {
                setBestPricePromiseDialogActive(productQuery.data);
              }}
            />
            <div className={"flex flex-col gap-4"}>
              {childProduct ? (
                <Card className={twMerge("bg-white", "border border-solid")}>
                  <CardHeader
                    className={twMerge("bg-white", "flex flex-col gap-2 p-4")}
                  >
                    <H2>{childProduct.name?.split(" • ")[1]}</H2>
                    <div className="flex flex-col gap-2">
                      <div className={"flex gap-1"}>
                        <DietaryDisplayList
                          dietaryRequirements={{
                            VG: Boolean(childProduct.vegan),
                            GF: Boolean(childProduct.glutenFree),
                            LF: Boolean(childProduct.lactoseFree),
                          }}
                          mode="badge"
                        />
                        <StockIndicator
                          stock={childProduct.availableStock}
                          canBackorder={childProduct.canBackorder}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className={"flex flex-col gap-4 p-4"}>
                    <div className={"flex justify-between"}>
                      <H5>Unit price (ex. VAT)</H5>
                      <div className={"flex items-center gap-2"}>
                        <H3 className="select-text">{formatCurrency(price)}</H3>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : null}
              <InfoCards />
            </div>
          </div>
        )}
      </DialogContent>
    </>
  );
};
